import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Cláusulas obrigatórias do aceite eletrônico (conforme contrato oficial)
const ACEITES = [
  ["aceite_li_integralmente", "Li integralmente este contrato"],
  ["aceite_sem_vinculo_empregaticio", "Compreendo que NÃO existe vínculo empregatício"],
  ["aceite_aceitar_recusar", "Compreendo que posso aceitar ou recusar serviços livremente"],
  ["aceite_garantia_30dias", "Compreendo as regras de garantia operacional de 30 dias"],
  ["aceite_regras_financeiras", "Compreendo as regras financeiras e divergências (Cláusula 13)"],
  ["aceite_auditoria_rastreabilidade", "Compreendo as regras de auditoria e rastreabilidade"],
  ["aceite_uso_imagem", "Compreendo a política de uso de imagem (Cláusula 16)"],
  ["aceite_mensalidade_pos_12m", "Compreendo a mensalidade de R$ 29,99 após 6 meses (Cláusula 24)"],
  ["aceite_termos_integrais", "Concordo integralmente com todos os termos do contrato"],
];

export default function ContratoMontador() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [aceites, setAceites] = useState(Object.fromEntries(ACEITES.map(([k]) => [k, false])));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#142766";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
      const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
      return { x, y };
    };
    const start = (e) => { drawing.current = true; const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const move = (e) => { if (!drawing.current) return; const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); };
    const end = () => { drawing.current = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start); c.addEventListener("touchmove", move); window.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move); window.removeEventListener("touchend", end);
    };
  }, []);

  const clearSig = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const todos = Object.values(aceites).every(Boolean);

  const submit = async () => {
    if (!todos) { toast.error("Aceite todas as declarações obrigatórias"); return; }
    const sig = canvasRef.current.toDataURL("image/png");
    setLoading(true);
    try {
      await api.post("/auth/contrato-aceite", {
        ...aceites,
        // Compat com backend antigo
        aceito_regras_operacionais: true,
        aceito_regras_garantia: true,
        aceito_retorno_30_dias: true,
        aceito_politica_bloqueio: true,
        aceito_auditoria: true,
        assinatura_digital: sig,
        dispositivo: navigator.userAgent,
      });
      toast.success("Contrato assinado com sucesso");
      setUser({ ...user, primeiro_acesso: false });
      navigate("/montador");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border sticky top-0 z-10 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 brand-bg" />
            <span className="font-heading font-extrabold">MontaJá</span>
          </div>
          <button onClick={logout} className="text-sm text-muted-foreground hover:text-foreground" data-testid="contrato-logout">
            Sair
          </button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="label-cap">Primeiro acesso · Aceite eletrônico</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-2">
          Contrato-Mestre de Parceria Operacional<br />
          <span className="text-xl">e Prestação de Serviços Autônomos</span>
        </h1>
        <p className="text-muted-foreground mt-3 text-sm">
          Leia integralmente. O aceite eletrônico possui validade jurídica equivalente à assinatura física (Cláusula 19).
        </p>

        {/* Identificação das partes */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="border border-border bg-[#F4F4F5] p-4 text-xs">
            <div className="label-cap mb-2">Contratante (operadora)</div>
            <div><strong>MontaJá — Uma rede. Um sistema. Todos ganham.</strong></div>
            <div className="text-muted-foreground">Plataforma tecnológica operadora</div>
          </div>
          <div className="border border-border bg-[#F4F4F5] p-4 text-xs">
            <div className="label-cap mb-2">Contratado (parceiro executor)</div>
            <div><strong>{user?.name || "—"}</strong></div>
            <div className="text-muted-foreground">{user?.email}</div>
          </div>
        </div>

        {/* Texto do contrato */}
        <div className="mt-8 border border-border bg-white p-8 text-sm leading-relaxed space-y-6 max-h-[60vh] overflow-y-auto"
             data-testid="contrato-texto">

          <Clause n="1" titulo="OBJETO">
            O presente contrato tem por objeto a disponibilização, intermediação e organização de oportunidades de prestação de serviços através da plataforma da CONTRATANTE.
            Os serviços poderão incluir: montagem de móveis; desmontagem; transferência; montagem em lojas; mostruários; assistência técnica; revisões; garantias; trocas autorizadas; inspeções; e demais serviços cadastrados.
            A CONTRATANTE não garante quantidade mínima de serviços ou faturamento.
          </Clause>

          <Clause n="2" titulo="NATUREZA DA RELAÇÃO">
            A CONTRATANTE atua exclusivamente como plataforma tecnológica, intermediadora operacional, organizadora de demandas e gestora de informações.
            As partes reconhecem que <strong>NÃO existe vínculo empregatício, subordinação jurídica, controle de jornada, exclusividade, salário fixo, habitualidade obrigatória, obrigação de comparecimento diário ou obrigação de aceitação de serviços.</strong>
          </Clause>

          <Clause n="3" titulo="AUTONOMIA DO PARCEIRO">
            O PARCEIRO possui total autonomia para aceitar ou recusar serviços, definir disponibilidade, capacidade operacional, cidades de atendimento, distância máxima, prestar serviços para terceiros e concorrentes, e contratar auxiliares quando permitido pela legislação.
            O PARCEIRO declara atuar por conta própria, assumindo os riscos de sua atividade econômica.
          </Clause>

          <Clause n="4" titulo="AUSÊNCIA DE GARANTIA DE DEMANDA">
            A CONTRATANTE não garante quantidade mínima de atendimentos, renda mínima, faturamento ou volume de serviços. A disponibilidade depende de demanda das empresas cadastradas, região, disponibilidade informada pelo parceiro e aceite voluntário.
          </Clause>

          <Clause n="5" titulo="CADASTRO E CAPACIDADE OPERACIONAL">
            O PARCEIRO é responsável por manter atualizados: endereço, telefone, e-mail, dados bancários, PIX, cidades atendidas, capacidade diária e semanal, e disponibilidade de agenda.
            A plataforma utilizará exclusivamente os limites definidos pelo próprio parceiro.
          </Clause>

          <Clause n="6" titulo="ACEITE DOS SERVIÇOS">
            Atendimentos são ofertados eletronicamente. O parceiro pode aceitar, recusar ou solicitar reagendamento. A aceitação caracteriza concordância com local, escopo, regras operacionais e exigências de evidências.
          </Clause>

          <Clause n="7" titulo="FERRAMENTAS E ESTRUTURA">
            O PARCEIRO declara possuir e manter sob sua responsabilidade ferramentas, equipamentos, transporte, celular, internet, EPIs e materiais necessários. Todos os custos operacionais são de responsabilidade exclusiva do PARCEIRO.
          </Clause>

          <Clause n="8" titulo="GARANTIA OPERACIONAL (30 DIAS)">
            Todo serviço executado possui garantia operacional de 30 (trinta) dias. Durante este período, revisões, correções e ajustes decorrentes da execução retornam ao parceiro responsável <strong>sem remuneração adicional</strong>. Após 30 dias aplicam-se as regras financeiras vigentes. A garantia possui natureza exclusivamente técnica.
          </Clause>

          <Clause n="9" titulo="EVIDÊNCIAS OPERACIONAIS">
            O parceiro concorda em registrar evidências obrigatórias: fotografias, vídeos, assinaturas, observações técnicas, relatórios e registros de ocorrência. A ausência das evidências poderá impedir a validação, aprovação financeira e encerramento do serviço.
          </Clause>

          <Clause n="10" titulo="RESPONSABILIDADE TÉCNICA">
            O PARCEIRO responderá por danos comprovadamente decorrentes de dolo, fraude, negligência grave ou descumprimento técnico comprovado, mediante análise das evidências disponíveis.
          </Clause>

          <Clause n="11" titulo="PAGAMENTOS">
            Os valores dos serviços não integram este contrato. Serão definidos por tabela operacional, anexo financeiro, aditivos comerciais ou regras específicas da empresa contratante. Cada empresa pode ter remuneração distinta.
          </Clause>

          <Clause n="12" titulo="FECHAMENTO FINANCEIRO">
            Fechamentos são automáticos pela plataforma, considerando apenas serviços concluídos, validados e aprovados conforme as regras vigentes.
          </Clause>

          <Clause n="13" titulo="DIVERGÊNCIAS FINANCEIRAS E RESPONSABILIDADE DE PAGAMENTO">
            <strong>Eventuais divergências financeiras (valores pagos, não recebidos, descontos, retenções, fechamento, notas fiscais) deverão ser tratadas diretamente entre o PARCEIRO e a EMPRESA CONTRATANTE</strong> responsável pelo atendimento, utilizando os canais disponibilizados.
            A EMPRESA OPERADORA atua exclusivamente como intermediadora tecnológica, <strong>não sendo responsável por pagamentos, inadimplência, atrasos, retenções ou divergências decorrentes de obrigações da EMPRESA CONTRATANTE</strong>.
            Divergências devem ser comunicadas em até 15 (quinze) dias após disponibilização do fechamento.
          </Clause>

          <Clause n="14" titulo="CONFIDENCIALIDADE">
            O PARCEIRO compromete-se a não divulgar dados de clientes, empresas, valores, processos internos, materiais da plataforma ou informações estratégicas. A obrigação permanece por 5 anos após o encerramento.
          </Clause>

          <Clause n="15" titulo="NÃO ALICIAMENTO">
            Durante a vigência e por 24 meses após o encerramento, o parceiro não poderá captar diretamente clientes da plataforma, contratar profissionais cadastrados, captar empresas cadastradas ou utilizar informações para concorrência desleal sem autorização formal.
          </Clause>

          <Clause n="16" titulo="USO DE IMAGEM E MATERIAL">
            O PARCEIRO autoriza utilização de fotos, vídeos, imagens dos serviços e registros operacionais para marketing, portfólio, treinamentos, apresentações comerciais, redes sociais e divulgação institucional, observada a legislação vigente.
          </Clause>

          <Clause n="17" titulo="LGPD">
            O parceiro compromete-se a utilizar dados acessados exclusivamente para execução dos serviços. É proibido compartilhar, vender, utilizar contatos para fins particulares ou armazenar informações sem autorização.
          </Clause>

          <Clause n="18" titulo="AUDITORIA E RASTREABILIDADE">
            A plataforma poderá registrar IP, data, hora, localização, dispositivo, histórico de acessos, aceite, comunicações e alterações. Os registros podem ser utilizados como prova para fins operacionais, administrativos e jurídicos.
          </Clause>

          <Clause n="19" titulo="ACEITE ELETRÔNICO">
            O aceite eletrônico realizado através da plataforma possui validade jurídica equivalente à assinatura física. Os registros eletrônicos podem ser utilizados como prova.
          </Clause>

          <Clause n="20" titulo="BLOQUEIOS OPERACIONAIS">
            A plataforma poderá aplicar bloqueios temporários ou permanentes em casos de faltas injustificadas, abandono de atendimento, fraude, falsificação, descumprimento de regras ou comportamento incompatível. Os bloqueios possuem natureza exclusivamente operacional.
          </Clause>

          <Clause n="21" titulo="SUSPENSÃO E REDISTRIBUIÇÃO">
            A CONTRATANTE poderá redistribuir serviços quando houver indisponibilidade, desligamento, bloqueio, ausência de resposta ou incapacidade operacional declarada pelo próprio parceiro.
          </Clause>

          <Clause n="22" titulo="PRAZO E RESCISÃO">
            Contrato por prazo indeterminado. Pode ser encerrado por qualquer das partes a qualquer momento. A rescisão não afasta obrigações anteriores.
          </Clause>

          <Clause n="23" titulo="FORO">
            Fica eleito o foro da comarca da sede da EMPRESA OPERADORA cadastrada na plataforma para dirimir eventuais controvérsias.
          </Clause>

          <Clause n="24" titulo="UTILIZAÇÃO DA PLATAFORMA E MENSALIDADE">
            Como incentivo à formação da rede, a utilização será <strong>gratuita durante os primeiros 6 (seis) meses</strong> contados da aprovação do cadastro.
            Após este período, mensalidade inicial de <strong>R$ 29,99</strong> por mês, por parceiro cadastrado. Não pagamento pode resultar em suspensão, bloqueio de novas ofertas e restrição de funcionalidades.
            A cobrança não garante quantidade mínima de serviços ou faturamento.
          </Clause>
        </div>

        {/* Declarações obrigatórias */}
        <div className="mt-8">
          <div className="label-cap mb-3">Declarações obrigatórias para aceite eletrônico</div>
          <div className="space-y-2">
            {ACEITES.map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 p-3 border border-border cursor-pointer hover:bg-secondary">
                <Checkbox checked={aceites[k]}
                  onCheckedChange={(v) => setAceites((a) => ({ ...a, [k]: !!v }))}
                  data-testid={`contrato-${k}`} />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Assinatura */}
        <div className="mt-8">
          <Label className="label-cap">Assinatura digital</Label>
          <div className="mt-2 border border-border bg-white">
            <canvas ref={canvasRef} width={680} height={180}
              className="w-full touch-none cursor-crosshair"
              data-testid="contrato-canvas" />
          </div>
          <button onClick={clearSig} className="mt-2 text-xs text-muted-foreground hover:underline"
            data-testid="contrato-limpar-assinatura">Limpar assinatura</button>
        </div>

        <Button disabled={!todos || loading} onClick={submit}
          className="mt-8 btn-sharp brand-bg text-white hover:bg-[#001A5E] h-12 px-10"
          data-testid="contrato-assinar">
          {loading ? "Registrando…" : "Aceitar e assinar contrato"}
        </Button>
      </div>
    </div>
  );
}

function Clause({ n, titulo, children }) {
  return (
    <div>
      <div className="font-heading font-bold text-sm">CLÁUSULA {n} — {titulo}</div>
      <p className="mt-1 text-sm text-foreground">{children}</p>
    </div>
  );
}
